/* Kanmon mosaic light-sweep — dependency-free <canvas> animation.
   Renders the mosaic shape in layer1-grid.js with a travelling green↔blue light
   sweep. Canvas is transparent; composites over any background.
   API: KanmonLightSweep.create(canvas, options) →
        { start, stop, resize, setOptions, getOptions, destroy }
   Options + integration: see DEV-HANDOFF.md. */
(function (global) {
  'use strict';

  const DEFAULTS = {
    grid: null,
    displayWidth: null,
    greenHex: '#418d29',
    blueHex: '#1E6781',
    sweepSeconds: 1.4,
    restSeconds: 0.5,
    brightness: 1.0,     // white sparkle intensity
    lightSize: 46,       // sparkle glow radius at the core (px); grows ~3x to the tip
    lightTrailMs: 150,   // length of the sparkle's afterglow trail
    waveSoftness: 0.16,  // softness of the colour wave edge
    waveLag: 0.16,       // how far the colour wave lags behind the light point
    originDrop: 40,      // how far below the bottom edge the flash ignites (off-screen)
    originShiftX: -130,  // nudge the light origin LEFT (px) so an early curve works
    waveOriginLeft: 150, // colour wave radiates from this far LEFT of the origin
    waveOriginDown: 60,  // …and this far below it (lower-left)
    curveAmount: 0.26,   // how much the light path bows (right-then-up)
    cursor: true,
    cursorStrength: 0.55,// max opacity drop under the cursor
    cursorRadius: 95,    // px reach around the cursor
    cursorTrailMs: 350,  // how long the dimming lingers
    background: null,
  };

  const clamp01 = (x) => (x < 0 ? 0 : x > 1 ? 1 : x);
  const lerp = (a, b, x) => a + (b - a) * x;
  function smoothstep(e0, e1, x) { const t = clamp01((x - e0) / (e1 - e0)); return t * t * (3 - 2 * t); }
  function hash(a, b) { let h = (a * 73856093) ^ (b * 19349663); h = (h ^ (h >> 13)) >>> 0; return (h % 10000) / 10000; }

  function hexHsl(hex) {
    const r = parseInt(hex.slice(1, 3), 16) / 255, g = parseInt(hex.slice(3, 5), 16) / 255, b = parseInt(hex.slice(5, 7), 16) / 255;
    const mx = Math.max(r, g, b), mn = Math.min(r, g, b); let h = 0, s = 0; const l = (mx + mn) / 2;
    if (mx !== mn) { const d = mx - mn; s = l > 0.5 ? d / (2 - mx - mn) : d / (mx + mn); if (mx === r) h = (g - b) / d + (g < b ? 6 : 0); else if (mx === g) h = (b - r) / d + 2; else h = (r - g) / d + 4; h /= 6; }
    return [h * 360, s, l];
  }
  function hsl(h, s, l) {
    h = (((h % 360) + 360) % 360) / 360;
    if (s === 0) { const v = l * 255; return [v, v, v]; }
    const q = l < 0.5 ? l * (1 + s) : l + s - l * s, p = 2 * l - q;
    const f = (t) => { if (t < 0) t += 1; if (t > 1) t -= 1; if (t < 1 / 6) return p + (q - p) * 6 * t; if (t < 1 / 2) return q; if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6; return p; };
    return [f(h + 1 / 3) * 255, f(h) * 255, f(h - 1 / 3) * 255];
  }
  // Recolour: keep each cell's source LIGHTNESS (carries the exact contrast /
  // dark core / fade from the PNG) and saturation texture; rotate to the chosen
  // base hue. SREF maps the source's median saturation onto the base colour's.
  function rgbToHsl(r, g, b) {
    r /= 255; g /= 255; b /= 255;
    const mx = Math.max(r, g, b), mn = Math.min(r, g, b); let h = 0, s = 0; const l = (mx + mn) / 2;
    if (mx !== mn) { const d = mx - mn; s = l > 0.5 ? d / (2 - mx - mn) : d / (mx + mn); if (mx === r) h = (g - b) / d + (g < b ? 6 : 0); else if (mx === g) h = (b - r) / d + 2; else h = (r - g) / d + 4; h /= 6; }
    return [h * 360, s, l];
  }
  const SREF = 0.78;

  function create(canvas, options) {
    if (!canvas) throw new Error('KanmonLightSweep.create: canvas required');
    const ctx = canvas.getContext('2d');
    let opts = Object.assign({}, DEFAULTS, options || {});
    const grid = opts.grid || global.KANMON_LAYER1_GRID;
    if (!grid) throw new Error('KanmonLightSweep: no grid data (load layer1-grid.js)');

    let cells = null, dpr = 1, sc = 1, drawSize = 0;
    let spineX = null, spineY = null, spineL = null, spineTotal = 1, spineN = 0;
    let rafId = null, running = false, startTime = 0, pausedAt = 0;
    const mouse = { x: -1e5, y: -1e5, active: false };
    let lastNow = 0;

    function rebuildGeometry() {
      dpr = Math.min(2, global.devicePixelRatio || 1);
      const dispW = opts.displayWidth || grid.width;
      sc = dispW / grid.width;
      canvas.width = Math.round(dispW * dpr);
      canvas.height = Math.round(grid.height * sc * dpr);
      canvas.style.width = dispW + 'px';
      canvas.style.height = (grid.height * sc) + 'px';
      drawSize = Math.ceil(grid.cell * sc * dpr) + 1;

      // Origin sits near the BOTTOM of the artboard (partly off-edge), in the
      // darkest point. Nudged right a couple of cells so a slight early curve
      // still rides the shape instead of running off it.
      const ox = grid.elbow[0] + opts.originShiftX;
      const oy = grid.height + opts.originDrop;
      const [pdx, pdy] = grid.dir;        // up-right heading, for locating the tip
      const cs = grid.cell, ofx = grid.offX, ofy = grid.offY;

      // ── curved spine: the light point rides this path ──────────────────
      // Reach to the farthest opaque cell up-right; a control point bows the
      // curve so it heads RIGHT first, then sweeps UP.
      let tipx = ox, tipy = oy, far = 0;
      for (let i = 0; i < grid.cells.length; i++) {
        const g = grid.cells[i];
        if (g[3] < 0.3) continue;
        const cx = ofx + (g[0] + 0.5) * cs, cy = ofy + (g[1] + 0.5) * cs;
        const proj = (cx - ox) * pdx + (cy - oy) * pdy;
        if (proj > far) { far = proj; tipx = cx; tipy = cy; }
      }
      const len = Math.hypot(tipx - ox, tipy - oy) || 1;
      const dx = (tipx - ox) / len, dy = (tipy - oy) / len;   // heading from the low origin
      const mx0 = (ox + tipx) / 2, my0 = (oy + tipy) / 2;
      // perpendicular pointing down-right of the travel direction
      const px = -dy, py = dx;
      const bow = opts.curveAmount * len;
      const cpx = mx0 + px * bow, cpy = my0 + py * bow;

      // sample the quadratic Bézier and build cumulative arc length
      const N = 90;
      const sx = new Float64Array(N), sy = new Float64Array(N), sl = new Float64Array(N);
      let prevx = ox, prevy = oy, acc = 0;
      for (let k = 0; k < N; k++) {
        const u = k / (N - 1), iu = 1 - u;
        const bx = iu * iu * ox + 2 * iu * u * cpx + u * u * tipx;
        const by = iu * iu * oy + 2 * iu * u * cpy + u * u * tipy;
        if (k > 0) acc += Math.hypot(bx - prevx, by - prevy);
        sx[k] = bx; sy[k] = by; sl[k] = acc; prevx = bx; prevy = by;
      }
      const total = acc || 1;
      // expose the spine (CSS px) so the travelling point can ride it in frame()
      spineX = new Float64Array(N); spineY = new Float64Array(N); spineL = sl; spineTotal = total; spineN = N;
      for (let k = 0; k < N; k++) { spineX[k] = sx[k] * sc; spineY[k] = sy[k] * sc; }

      // per-cell radial distance → drives the colour WAVE. The wave radiates
      // from a point LOWER and to the LEFT of the light origin, so the bottom-
      // left fills in step with (not behind) the top-right.
      const wox = ox - opts.waveOriginLeft, woy = oy + opts.waveOriginDown;
      let maxR = 1;
      const rads = new Float64Array(grid.cells.length);
      for (let i = 0; i < grid.cells.length; i++) {
        const g = grid.cells[i];
        const cx = ofx + (g[0] + 0.5) * cs, cy = ofy + (g[1] + 0.5) * cs;
        const r = Math.hypot(cx - wox, cy - woy);
        rads[i] = r; if (r > maxR) maxR = r;
      }

      // Tile cells edge-to-edge: each cell's right/bottom edge is computed from
      // the NEXT integer boundary, so neighbours abut exactly — no 1px overlap
      // (which would double-up translucent alpha into seam lines) and no gaps.
      const step = cs * sc * dpr;
      const ox0 = Math.round(ofx * sc * dpr), oy0 = Math.round(ofy * sc * dpr);
      cells = new Array(grid.cells.length);
      for (let i = 0; i < grid.cells.length; i++) {
        const g = grid.cells[i];
        const cx = ofx + (g[0] + 0.5) * cs, cy = ofy + (g[1] + 0.5) * cs;
        const hsv = rgbToHsl(g[2], g[3], g[4]);   // source colour
        const px = ox0 + Math.round(g[0] * step), py = oy0 + Math.round(g[1] * step);
        cells[i] = {
          x: px,
          y: py,
          w: ox0 + Math.round((g[0] + 1) * step) - px,
          h: oy0 + Math.round((g[1] + 1) * step) - py,
          cx: cx * sc, cy: cy * sc,             // centre in CSS px
          ss: hsv[1], sl: hsv[2], d: 1 - hsv[2], a: g[5],
          rad: rads[i] / maxR,                  // 0 at elbow → 1 at farthest cell
          stag: (hash(g[0] + 1, g[1] + 1) - 0.5) * 0.05,
          ex: 0,        // cursor dim
          spark: 0,     // travelling-light afterglow
        };
      }
      rebuildColors();
    }

    let GA, BA;
    function rebuildColors() {
      const gh = hexHsl(opts.greenHex), bh = hexHsl(opts.blueHex);
      const gMul = gh[1] / SREF, bMul = bh[1] / SREF;
      if (cells) for (const c of cells) {
        const g = hsl(gh[0], clamp01(c.ss * gMul), c.sl); c.gr = g[0]; c.gg = g[1]; c.gb = g[2];
        const b = hsl(bh[0], clamp01(c.ss * bMul), c.sl); c.br = b[0]; c.bg = b[1]; c.bb = b[2];
      }
    }
    rebuildGeometry();

    function frame(now) {
      const dt = lastNow ? Math.min(60, now - lastNow) : 16; lastNow = now;
      if (opts.background) { ctx.fillStyle = opts.background; ctx.fillRect(0, 0, canvas.width, canvas.height); }
      else ctx.clearRect(0, 0, canvas.width, canvas.height);

      const elapsed = (now - startTime) / 1000;
      const slot = opts.sweepSeconds + opts.restSeconds;
      const passIndex = Math.floor(elapsed / slot);
      const phase = clamp01((elapsed - passIndex * slot) / opts.sweepSeconds);
      const evenPass = (passIndex % 2) === 0;

      // ── colour WAVE: lags behind the point and expands from the origin, so
      //    the colour flows THROUGH after the light has zipped past. ──
      const waveFront = smoothstep(opts.waveLag, 1.0, phase) * 1.15;
      const soft = Math.max(0.02, opts.waveSoftness);

      // ── travelling POINT: enters already in motion (no hold) and zips off
      //    immediately, growing ~3x as it spreads up the spine.
      const sp = 1 - (1 - phase) * (1 - phase);     // ease-OUT: fast departure
      const targetLen = sp * spineTotal;
      let k = 0; while (k < spineN - 1 && spineL[k + 1] < targetLen) k++;
      const segL = (spineL[k + 1] - spineL[k]) || 1;
      const fr = clamp01((targetLen - spineL[k]) / segL);
      const sparkX = lerp(spineX[k], spineX[Math.min(spineN - 1, k + 1)], fr);
      const sparkY = lerp(spineY[k], spineY[Math.min(spineN - 1, k + 1)], fr);
      const sparkActive = phase > 0.001 && phase < 0.999;
      const sizeMul = 1.6 + 1.7 * sp;               // begins larger, grows to the tip
      const sparkR = opts.lightSize * sizeMul * sc; // glow radius in CSS px
      const sparkR2 = sparkR * sparkR;
      // fade the light in (not an abrupt pop) and out at the end
      const env = smoothstep(0, 0.16, phase) * (1 - smoothstep(0.86, 1.0, phase));
      const bright = opts.brightness * (1 - 0.15 * sp) * env;
      const sparkDecay = Math.exp(-dt / Math.max(30, opts.lightTrailMs));

      const curOn = opts.cursor, curStr = opts.cursorStrength, curR = opts.cursorRadius;
      const decay = Math.exp(-dt / Math.max(40, opts.cursorTrailMs));

      for (let i = 0; i < cells.length; i++) {
        const c = cells[i];
        // colour from the radial wave (ping-pong each pass)
        const passed = smoothstep(c.rad - soft, c.rad + soft, waveFront + c.stag);
        const mix = evenPass ? passed : 1 - passed;
        let rr = lerp(c.gr, c.br, mix), gg = lerp(c.gg, c.bg, mix), bb = lerp(c.gb, c.bb, mix);
        let a = c.a;

        // travelling white sparkle + short afterglow trail
        c.spark *= sparkDecay;
        if (sparkActive) {
          const ddx = c.cx - sparkX, ddy = c.cy - sparkY;
          const dd = ddx * ddx + ddy * ddy;
          if (dd < sparkR2) {
            // heavily feathered glow — soft Gaussian falloff, no hard disc edge
            const t = dd / sparkR2;                // 0 center → 1 at radius
            const s = Math.exp(-4.0 * t) * bright;
            if (s > c.spark) c.spark = s;
          }
        }
        if (c.spark > 0.002) {
          const w = Math.min(1, c.spark);
          rr += (255 - rr) * w; gg += (255 - gg) * w; bb += (255 - bb) * w;
          a = a + (1 - a) * w * c.a;               // glint scales with the cell's own
                                                   // opacity — faint edges stay faded
        }

        // cursor: dim nearby shape pixels, with a lingering trail
        if (curOn) {
          c.ex *= decay;
          if (mouse.active) {
            const dd = Math.hypot(c.cx - mouse.x, c.cy - mouse.y);
            if (dd < curR) { const imp = (1 - dd / curR); if (imp > c.ex) c.ex = imp; }
          }
          if (c.ex > 0.002) a *= (1 - c.ex * curStr);
        }

        if (a < 0.004) continue;
        ctx.fillStyle = 'rgba(' + (rr | 0) + ',' + (gg | 0) + ',' + (bb | 0) + ',' + a.toFixed(3) + ')';
        ctx.fillRect(c.x, c.y, c.w, c.h);
      }
    }

    function tick(now) { frame(now); rafId = global.requestAnimationFrame(tick); }

    function onMove(e) {
      const r = canvas.getBoundingClientRect();
      mouse.x = (e.clientX - r.left) * (grid.width / r.width) * sc;
      mouse.y = (e.clientY - r.top) * (grid.height / r.height) * sc;
      mouse.active = true;
    }
    function onLeave() { mouse.active = false; }
    // Listen on the window, not the canvas: as a background layer the canvas is
    // typically pointer-events:none and would never receive these. Coords map
    // through the canvas rect and cells only react within cursorRadius, so a
    // far-off pointer has no effect.
    global.addEventListener('pointermove', onMove);
    document.documentElement.addEventListener('pointerleave', onLeave);

    function start() { if (running) return ctrl; running = true; lastNow = 0; startTime = global.performance.now() - pausedAt; rafId = global.requestAnimationFrame(tick); return ctrl; }
    function stop() { if (!running) return ctrl; running = false; pausedAt = global.performance.now() - startTime; if (rafId) global.cancelAnimationFrame(rafId); rafId = null; return ctrl; }
    function setOptions(patch) {
      const prev = opts; opts = Object.assign({}, opts, patch || {});
      if (['displayWidth', 'curveAmount', 'originDrop', 'originShiftX', 'waveOriginLeft', 'waveOriginDown'].some((k) => opts[k] !== prev[k])) rebuildGeometry();
      else if (['greenHex', 'blueHex'].some((k) => opts[k] !== prev[k])) rebuildColors();
      if (!running) frame(global.performance.now());
      return ctrl;
    }
    function resize(w) { return setOptions({ displayWidth: w }); }
    function getOptions() { return Object.assign({}, opts); }
    function destroy() { stop(); global.removeEventListener('pointermove', onMove); document.documentElement.removeEventListener('pointerleave', onLeave); cells = null; }

    startTime = global.performance.now();
    frame(startTime);
    const ctrl = { start, stop, setOptions, getOptions, resize, destroy, canvas, grid };
    return ctrl;
  }

  global.KanmonLightSweep = { create, DEFAULTS };
})(typeof window !== 'undefined' ? window : this);
